import { Box, Grid, Typography } from "@mui/material";
import React from "react";
import { Button, ButtonVariantOption } from "../Button";
import { StyledDataGrid } from "../DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { type GridSelectionModel, type GridColDef } from "@mui/x-data-grid-pro";
import { type SearchResultRow } from "@/types/kaokuNumber";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { useIsMobile } from "@/hooks/useIsMobile";
import { SelectableCardList } from "./SelectableCardList";

interface Props {
  title: string;
  buttonLabel: string;
  dataType: "chiban" | "kaokuNumber";
  disabled: boolean;
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  loading: boolean;
  rows: SearchResultRow[];
  selectedRowIds: GridSelectionModel;
  setOpenAddRowsModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  columns?: GridColDef[];
  height?: number;
  hideListButton?: boolean;
}

const baseColumnProps = {
  sortable: false,
  align: "center" as const,
  headerAlign: "center" as const,
};

const prefectureColumn: GridColDef = {
  field: "prefectureName",
  headerName: "都道府県",
  width: 150,
  ...baseColumnProps,
};

const locationColumn: GridColDef = {
  field: "location",
  headerName: "所在",
  width: 400,
  ...baseColumnProps,
};

const chibanColumn: GridColDef = {
  field: "chiban",
  headerName: "地番",
  width: 240,
  ...baseColumnProps,
  renderCell: (params) => {
    const value = (params.value as string) ?? "";
    return NYHalfWidthNumberToFillWidthNumber(value);
  },
};

const kaokuNumberColumn: GridColDef = {
  field: "kaokuNumber",
  headerName: "家屋番号",
  width: 240,
  ...baseColumnProps,
  renderCell: (params) => {
    const value = (params.value as string) ?? "";
    return NYHalfWidthNumberToFillWidthNumber(value);
  },
};

const defaultChibanResultColumns: GridColDef[] = [
  prefectureColumn,
  locationColumn,
  chibanColumn,
  kaokuNumberColumn,
];

const defaultKaokuNumberResultColumns: GridColDef[] = [
  prefectureColumn,
  locationColumn,
  kaokuNumberColumn,
  chibanColumn,
];

/**
 * 検索結果の表示領域（汎用版）
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const ViewAreaSearchResult: React.FC<Props> = (props) => {
  const {
    title,
    buttonLabel,
    dataType,
    disabled,
    handleRowSelectionChange,
    loading,
    rows,
    selectedRowIds,
    setOpenAddRowsModalFlag,
    columns,
    height = 300,
    hideListButton = false,
  } = props;

  const isMobile = useIsMobile();

  const getColumns = (): GridColDef[] => {
    if (columns) return columns;
    return dataType === "chiban"
      ? defaultChibanResultColumns
      : defaultKaokuNumberResultColumns;
  };

  const listButton = (
    <Button
      label={buttonLabel}
      size={SizingWrapperStyle.INHERIT}
      variant={ButtonVariantOption.Outlined}
      onClick={() => {
        setOpenAddRowsModalFlag(true);
      }}
      disabled={disabled}
    />
  );

  return (
    <>
      <Typography variant="subtitle1" sx={{ py: 1, fontWeight: "bold" }}>
        検索結果
      </Typography>
      {/* SP は表をカードにし、ボタンは横いっぱいにせず右寄せ */}
      {isMobile ? (
        <SelectableCardList
          title={title}
          columns={getColumns()}
          rows={rows}
          selectedRowIds={selectedRowIds}
          handleRowSelectionChange={handleRowSelectionChange}
          loading={loading}
          emptyMessage="検索結果はありません"
        />
      ) : (
        <Box sx={{ height }}>
          <StyledDataGrid
            rowHeight={36}
            headerHeight={40}
            disableColumnMenu
            disableSelectionOnClick
            checkboxSelection
            rows={rows}
            columns={getColumns()}
            selectionModel={selectedRowIds}
            onSelectionModelChange={handleRowSelectionChange}
            loading={loading}
          />
        </Box>
      )}
      {!hideListButton &&
        (isMobile ? (
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 2 }}
          >
            <Box sx={{ width: "fit-content" }}>{listButton}</Box>
          </Box>
        ) : (
          <Grid container mt={1} mb={2} justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4} lg={3} mx={2}>
              {listButton}
            </Grid>
          </Grid>
        ))}
    </>
  );
};
